import React, { ReactNode, useContext } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import _ from 'underscore';
import { HelpPopup } from '@bingads-webui-react/help-popup';
import { PermissionsContext } from '@bingads-webui-react/hoc-with-permissions';
import { isNavigationRefreshOn } from '@bingads-webui-react/preference-utility';

/**
 * SectionGroup Props
 * @property {String} title - title of the Section
 * @property {Array/String/Object} titleClass - array of classes to decorate sub titles
 * @property {String} helpPopupTopic - will add help popup if provided
 * @property {String} helpPopupAriaLabel - aria label of the help popup
 * @property {String} helpPopupAriaLabelledBy - aria labelledby of the help popup
 * @property {Node} children - the content of the Section
*/

export interface SectionGroupProps {
  name?: string;
  title?: string;
  titleClass?: Parameters<typeof classNames>[0];
  helpPopupTopic?: string;
  helpPopupAriaLabel?: string;
  helpPopupAriaLabelledBy?: string;
  children: ReactNode;
}

export interface SectionTagProps {
  tagClass?: Parameters<typeof classNames>[0];
  children: ReactNode;
}

export function SectionGroup(props: SectionGroupProps) {
  const {
    name, title, titleClass, helpPopupTopic, helpPopupAriaLabel, helpPopupAriaLabelledBy, children,
  } = props;
  const sectionClassName = `section-group-${name || _.uniqueId()}`;

  return (
    <div className={classNames('section', sectionClassName)}>
      <div className="row">
        <div className={classNames('page-header', titleClass)}>
          {title || ''}
          {
            helpPopupTopic &&
            <HelpPopup
              topic={helpPopupTopic}
              aria-label={helpPopupAriaLabel}
              aria-labelledby={helpPopupAriaLabelledBy}
            />
          }
        </div>
      </div>
      <div className="section-body">
        {children}
      </div>
    </div>
  );
}

SectionGroup.propTypes = {
  name: PropTypes.string,
  title: PropTypes.string,
  titleClass: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(PropTypes.string),
    PropTypes.object,
  ]),
  helpPopupTopic: PropTypes.string,
  helpPopupAriaLabel: PropTypes.string,
  helpPopupAriaLabelledBy: PropTypes.string,
  children: PropTypes.node.isRequired,
};

SectionGroup.defaultProps = {
  name: '',
  title: '',
  titleClass: ['h3', 'type-bold'],
  helpPopupTopic: '',
  helpPopupAriaLabel: '',
  helpPopupAriaLabelledBy: '',
};

/**
 * SectionTag Props
 * @property {Array/String/Object} tagClass - array of classes to decorate the component
 * @property {Node} children - the content of the component
*/

export function SectionTag(props: SectionTagProps) {
  const { tagClass, children } = props;
  const permissions = useContext(PermissionsContext);

  return (
    <section className={classNames('row', 'content-area', tagClass)}>
      <div className="page-content col-sm-12" style={isNavigationRefreshOn(permissions) ? { position: 'static' } : {}}>
        {children}
      </div>
    </section>
  );
}

SectionTag.propTypes = {
  tagClass: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(PropTypes.string),
    PropTypes.object,
  ]),
  children: PropTypes.node.isRequired,
};

SectionTag.defaultProps = { tagClass: '' };
